import {AUTOCOMPLETE_HINT_SELECTED_EVENT, type AutocompleteController} from './utils';
import {map} from 'rxjs';
import {distinctUntilChanged, filter, tap} from 'rxjs/operators';
import {AUTOCOMPLETE_INPUT_EVENT} from './utils';
import type {ActionReturn} from 'svelte/action';
import {cleanup} from 'shared/utils/custom-utils';

interface Attributes {
    'on:autocomplete-input'?: (e: CustomEvent<string>) => void;
    'on:autocomplete-hint-select'?: (e: CustomEvent<boolean>) => void;
}

export function autocomplete(inputElement: HTMLInputElement, autocompleteController: AutocompleteController): ActionReturn<undefined, Attributes> {
    inputElement.setAttribute('role', 'combobox')
    inputElement.setAttribute('autocomplete', 'off')
    inputElement.setAttribute('autocapitalize', 'off')
    inputElement.setAttribute('autocorrect', 'off')
    inputElement.setAttribute('spellcheck', 'false')
    inputElement.setAttribute('aria-autocomplete', 'list')
    inputElement.setAttribute('aria-haspopup', 'listbox')
    inputElement.setAttribute('aria-controls', autocompleteController.resultListId)

    const subscriptionForExpanded = autocompleteController.getState$()
        .pipe(
            map(({expanded}) => expanded),
            distinctUntilChanged())
        .subscribe((expanded) => inputElement.setAttribute('aria-expanded', expanded.toString()));

    const subscriptionForSelectedIndex = autocompleteController.getState$()
        .pipe(
            map(({selectedIndex}) => selectedIndex),
            distinctUntilChanged())
        .subscribe((selectedIndex) => {
            if (selectedIndex > -1) {
                inputElement.setAttribute('aria-activedescendant', `autocomplete-result-${selectedIndex}`);
            } else {
                inputElement.removeAttribute('aria-activedescendant');
            }

            inputElement.dispatchEvent(new CustomEvent<boolean>(AUTOCOMPLETE_HINT_SELECTED_EVENT, {detail: selectedIndex > -1}));
        });

    const subscriptionForInputValue = autocompleteController.getState$()
        .pipe(
            map(({inputValue}) => inputValue),
            distinctUntilChanged(),
            filter<string>((inputValue) => inputValue !== inputElement.value),
            tap<string>((inputValue) => inputElement.value = inputValue))
        .subscribe((inputValue) => inputElement.dispatchEvent(new CustomEvent<string>(AUTOCOMPLETE_INPUT_EVENT, {detail: inputValue})));

    const abortController = new AbortController();
    inputElement.addEventListener('input', (e) => autocompleteController.handleInput(e), {signal: abortController.signal});
    inputElement.addEventListener('keydown', (e) => autocompleteController.core.handleKeyDown(e), {signal: abortController.signal});
    inputElement.addEventListener('focus', (e) => autocompleteController.core.handleFocus(e), {signal: abortController.signal});
    inputElement.addEventListener('blur', (e) =>autocompleteController.core.handleBlur(e), {signal: abortController.signal});

    return {
        destroy() {
            cleanup(subscriptionForExpanded, subscriptionForSelectedIndex, subscriptionForInputValue, abortController);
        }
    }
}